import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import addressIcon from '../../assets/designSystem/icons/emergency/Address.svg';
import callIcon from '../../assets/designSystem/icons/emergency/call.svg';
import Airplane from '../../assets/designSystem/icons/airplane.svg';
import Ambulance from '../../assets/designSystem/icons/ambulance.svg';
import Hospital from '../../assets/designSystem/icons/hospital.svg';
import closeButtonPic from '../../assets/designSystem/icons/close.svg';
import MetricsContext from '../../context/MetricsContext';
import PageNames from '../../utils/PageNames';
import PageTypes from '../../utils/PageTypes';
import ButtonNames from '../../utils/ButtonNames';
import { normalizeCountyName } from '../../utils';
import './style.css';
import BaseLayout from '../../commons/BaseLayout';

const contactData = {
  Nairobi: [
    { agency: 'Intensive Care Air Ambulance', telephone: '020-601772' },
    { agency: 'AAR Hospital', telephone: '+254 730 655000' },
    { agency: 'OCOA Ambulance', telephone: '+254 758692000' },
    { agency: "St. John's Ambulances", telephone: '0721-225285' },
    { agency: 'Avenue Rescue Services', telephone: '020-3743858' },
    { agency: "Gertrude Children's Hospital", telephone: '020 720 6000' },
    { agency: 'Nairobi East Hospital Ambulance', telephone: '0111435797' },
    { agency: 'Eplus', telephone: '0700395395' },
    { agency: 'RFH', telephone: '0741574782' },
    { agency: 'Mediheal Ambulance Parklands', telephone: '0706000888' },
    { agency: 'Quick Safe Ambulance Nairobi', telephone: '0721853796' },
    { agency: 'Avenue Parklands', telephone: '0711060150 / 0711060175' },
    { agency: 'Urban Safety Ambulance', telephone: '0724626156 / 0722162309' },
    { agency: "St Mary's Hospital Langata Ambulance", telephone: '0777663441' },
    { agency: 'EOC', telephone: '1508' },
  ],
  Kisii: [{ agency: 'Nyanchwa Ambulance Kisii', telephone: '0723623880' }],
  Kiambu: [
    { agency: 'Neema Ambulance Kahawa Sukari', telephone: '0759263586' },
    { agency: 'Avenue Hospital Kiambu', telephone: '0711060800 / 0715869147' },
    { agency: 'AAR Hospital Kiambu', telephone: '0709000000' },
  ],
  Kajiado: [{ agency: "Nairobi Women's Kitengela", telephone: '0717723677' }],
  Nakuru: [
    { agency: "Nairobi Women's Nakuru", telephone: '0707957840' },
    { agency: 'NWH Ambulance Nakuru', telephone: '070957840' },
  ],
  Thika: [{ agency: 'Avenue Thika', telephone: '0711060800 / 0715869147' }],
};

const REGION_OPTIONS = Object.keys(contactData);

function getInitialRegion() {
  if (localStorage.getItem('country') !== 'Kenya') {
    return 'Nairobi';
  }

  const storedRegion = normalizeCountyName(localStorage.getItem('county'));
  return contactData[storedRegion] ? storedRegion : 'Nairobi';
}

function getIconForAgency(agency) {
  const agencyLower = agency.toLowerCase();

  if (agencyLower.includes('ambulance')) return Ambulance;
  if (agencyLower.includes('hospital')) return Hospital;
  if (agencyLower.includes('air')) return Airplane;

  return Hospital;
}

function getTelephoneHref(telephone) {
  const primaryNumber = telephone.split('/')[0].replace(/[^\d+]/g, '');
  return `tel:${primaryNumber}`;
}

export default function Emergency() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateUserMetric } = useContext(MetricsContext);
  const startDate = useRef(new Date());
  const sessionId = localStorage.getItem('sessionId');
  const [selectedRegion, setSelectedRegion] = useState(getInitialRegion);
  const [showRegionPicker, setShowRegionPicker] = useState(false);

  const updateUserMetricRef = useRef(updateUserMetric);
  useEffect(() => {
    updateUserMetricRef.current = updateUserMetric;
  });

  useEffect(() => {
    updateUserMetricRef.current({
      name: PageNames.EMERGENCY_CONTACTS_MODAL,
      pageType: PageTypes.DEFAULT,
    });
  }, [sessionId]);

  const updateMetrics = (interactionName) => {
    const completedAt = new Date();

    updateUserMetric({
      name: PageNames.EMERGENCY_CONTACTS_MODAL,
      pageType: PageTypes.DEFAULT,
      duration: Math.abs(completedAt - startDate.current),
      interactions: [
        {
          name: interactionName,
          time: completedAt,
        },
      ],
    });
  };

  const handleContactClick = async (telephone) => {
    let interactionName = 'telephone selected';

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(telephone);
        interactionName = 'telephone copied to clipboard';
      }
    } catch {
      interactionName = 'telephone selected';
    }

    updateMetrics(interactionName);
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setShowRegionPicker(false);
    localStorage.setItem('country', 'Kenya');
    localStorage.setItem('county', region);
    updateMetrics(`region changed:${region}`);
  };

  const regionContacts = contactData[selectedRegion] || contactData.Nairobi;

  return (
    <BaseLayout>
      <div className="home-emergency__header">
        <div className="home-emergency__header-content">
          <button
            type="button"
            className="home-emergency__change-region-button"
            aria-expanded={showRegionPicker}
            aria-controls="home-emergency-region-options"
            onClick={() => setShowRegionPicker((current) => !current)}
          >
            <img src={addressIcon} alt="" width={16} height={16} />
            <span>{t('emergency.changeRegion')}</span>
          </button>

          <button
            type="button"
            className="home-emergency__close-button"
            aria-label={t('emergency.closeContacts')}
            onClick={() => {
              updateMetrics(ButtonNames.CLOSE);
              navigate('/homepage');
            }}
          >
            <img src={closeButtonPic} alt="" className="home-emergency__close-icon" />
          </button>
        </div>

        {showRegionPicker && (
          <fieldset id="home-emergency-region-options" className="home-emergency__region-picker">
            <legend className="home-emergency__region-legend">{t('emergency.regionPickerLabel')}</legend>
            {REGION_OPTIONS.map((region) => (
              <button
                type="button"
                key={region}
                className={`home-emergency__region-option ${
                  selectedRegion === region ? 'home-emergency__region-option--selected' : ''
                }`}
                aria-pressed={selectedRegion === region}
                onClick={() => handleRegionChange(region)}
              >
                {region}
              </button>
            ))}
          </fieldset>
        )}

        <div className="home-emergency__summary">
          <h3 className="home-emergency__title">
            <img src={callIcon} alt="" width={16} height={16} /> {t('emergency.title')}
          </h3>
          <p className="home-emergency__region">{t('emergency.regionLabel', { region: selectedRegion })}</p>
        </div>
      </div>

      <div className="home-emergency__contacts">
        {regionContacts.map((contact) => (
          <a
            className="emergency__contact home-emergency__contact"
            key={contact.agency}
            href={getTelephoneHref(contact.telephone)}
            onClick={() => handleContactClick(contact.telephone)}
          >
            <span className="emergency__contact__header">
              <img src={getIconForAgency(contact.agency)} alt="" width={16} height={16} />
              <span className="emergency__contact__name">{contact.agency}</span>
            </span>
            <span>{contact.telephone}</span>
          </a>
        ))}
      </div>
    </BaseLayout>
  );
}
